import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { testBtnOutline, testFocusRing } from '../test/testUiClasses';
import TutorialSessionModal from './TutorialSessionModal';
import { tutorialAccentClasses } from './tutorialSessions';
import TutorialVisual from './TutorialVisual';
import { useVisibleTutorialSessions } from './useVisibleTutorialSessions';

function TutorialHub({ t }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSession, setActiveSession] = useState(null);
  const visibleSessions = useVisibleTutorialSessions();

  useEffect(() => {
    const sessionId = searchParams.get('session');
    if (!sessionId) return;
    const match = visibleSessions.find((s) => s.id === sessionId);
    if (match) setActiveSession(match);
  }, [searchParams, visibleSessions]);

  const openSession = (session) => {
    setActiveSession(session);
    setSearchParams({ session: session.id }, { replace: true });
  };

  const closeSession = () => {
    setActiveSession(null);
    if (searchParams.get('session')) {
      searchParams.delete('session');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const recommended = visibleSessions[0] || null;

  return (
    <section>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-link uppercase tracking-wide">
            {t('help.tutorials')}
          </p>
          <h1 className="text-2xl md:text-3xl font-bold text-ink mt-1">
            {t('navigation.helpCentre')}
          </h1>
          <p className="text-ink-muted mt-2 max-w-2xl text-justify">
            {t('help.tutorialHub.subtitle')}
          </p>
        </div>
        <span className="w-fit shrink-0 rounded-md border border-link/30 bg-accent/25 text-blue-800 px-3 py-2 text-sm font-semibold self-start">
          {t('help.tutorialHub.count', { count: visibleSessions.length })}
        </span>
      </div>

      {visibleSessions.length === 0 ? (
        <p className="text-ink-muted text-center py-8">{t('help.tutorialHub.noSessions')}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 grid-flow-dense">
          {visibleSessions.map((session, index) => {
            const accent = tutorialAccentClasses[session.accent] || tutorialAccentClasses.blue;
            const isLarge = visibleSessions.length > 1 && (index === 0 || index === visibleSessions.length - 1);

            return (
              <button
                key={session.id}
                type="button"
                onClick={() => openSession(session)}
                style={{ minHeight: isLarge ? 240 : 190 }}
                className={`group overflow-hidden rounded-lg border bg-surface text-left shadow-sm hover:shadow-md hover:border-link/30 transition-shadow ${testFocusRing} ${
                  isLarge ? 'sm:col-span-2' : ''
                }`}
              >
                <div className={`h-28 ${accent.bg} border-b ${accent.border}`}>
                  <TutorialVisual variant={session.visual} accent={accent} />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className={`text-xs font-semibold border rounded-md px-2 py-1 ${accent.badge}`}>
                      {t(session.durationKey)}
                    </span>
                    <span className="text-xs font-semibold text-link group-hover:text-link-hover">
                      {t('help.tutorialHub.preview')}
                    </span>
                  </div>
                  <h3 className="font-bold text-blue-800 leading-snug">{t(session.titleKey)}</h3>
                  <p className="text-sm text-ink-muted mt-2 line-clamp-3">
                    {t(session.descriptionKey)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {recommended && (
        <div className="mt-5 rounded-lg border border-link/30 bg-accent/25 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-blue-900">{t('help.tutorialHub.helpNote')}</p>
          <button
            type="button"
            onClick={() => openSession(recommended)}
            className={testBtnOutline}
          >
            {t('help.tutorialHub.replayRecommended')}
          </button>
        </div>
      )}

      <TutorialSessionModal
        t={t}
        session={activeSession}
        onClose={closeSession}
      />
    </section>
  );
}

export default TutorialHub;
